import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import apiClient from '../../lib/api-client';

// Types

export interface SyncStatusResponse {
  policies: {
    lastSyncAt: string | null;
    lastSyncId: string | null;
    totalRecordsSynced: number;
  };
  clients: {
    lastSyncAt: string | null;
    lastSyncId: string | null;
    totalRecordsSynced: number;
  };
}

export interface SyncResult {
  syncId: string;
  success: boolean;
  recordsProcessed: number;
  recordsInserted: number;
  recordsUpdated: number;
  recordsFailed: number;
  startedAt: string;
  completedAt: string;
  durationMs: number;
  syncType: 'full' | 'incremental';
  error?: string;
}

export interface ScoringStatsResponse {
  totalOpportunities: number;
  averageScore: number;
  scoreDistribution: Array<{ bucket: string; count: number }>;
  totalEstimatedValue: number;
  lastScoredAt: string | null;
}

export interface ScoringResult {
  success: boolean;
  message: string;
  stats: {
    totalPolicies: number;
    policiesScored: number;
    policiesSkipped: number;
    skipReasons: Record<string, number>;
    policiesMeetingThreshold: number;
    opportunitiesCreated: number;
    opportunitiesUpdated: number;
    averageScore: number;
    scoreDistribution: Array<{ bucket: string; count: number; percentage: number }>;
    processingTimeMs: number;
    policiesPerSecond: number;
  };
}

export interface DbStatusResponse {
  tableCounts: { policies: number; clients: number };
  lineOfBusinessDistribution: Array<{ value: string | null; count: number }>;
  syncMetadata: Array<Record<string, unknown>>;
  recentSyncHistory: Array<Record<string, unknown>>;
  recentFirelightMappingRuns: Array<Record<string, unknown>>;
  recentScoringJobs: Array<Record<string, unknown>>;
  recentIngestionLogs: Array<Record<string, unknown>>;
  firelightMappings: {
    productMappings: number;
    verifiedProductMappings: number;
    launchReadyProductMappings: number;
    carrierMappings: number;
    verifiedCarrierMappings: number;
    lastMappedAt: string | null;
  };
}

export interface IngestionStatusResponse {
  latestRun: {
    id: string;
    source: string;
    status: string;
    products_processed: number | null;
    carriers_processed: number | null;
    errors: unknown[] | null;
    started_at: string;
    completed_at: string | null;
  } | null;
  productCounts: Record<string, number>;
}

export interface IngestionResult {
  success: boolean;
  logId: string;
  carriersUpserted: number;
  sourcesUpserted: number;
  productsUpserted: number;
  errors: Array<{ entity: string; id: string; error: string }>;
  durationMs: number;
}

export interface EndpointHealthMetric {
  method: string;
  route: string;
  count: number;
  errorCount: number;
  avgMs: number;
  minMs: number;
  maxMs: number;
  p95Ms: number;
  lastMs: number;
  lastStatusCode: number;
  lastSeenAt: string;
}

export interface HealthConsoleResponse {
  startedAt: string;
  generatedAt: string;
  endpoints: EndpointHealthMetric[];
}

// Query Keys
export const pipelineKeys = {
  all: ['pipeline'] as const,
  syncStatus: () => [...pipelineKeys.all, 'sync-status'] as const,
  scoringStats: () => [...pipelineKeys.all, 'scoring-stats'] as const,
  ingestionStatus: () => [...pipelineKeys.all, 'ingestion-status'] as const,
  dbStatus: () => [...pipelineKeys.all, 'db-status'] as const,
  healthConsole: () => [...pipelineKeys.all, 'health-console'] as const,
};

// API Functions
async function fetchSyncStatus(): Promise<SyncStatusResponse> {
  const response = await apiClient.get<SyncStatusResponse>('/jobs/sync-policies/status');
  return response.data;
}

async function fetchScoringStats(): Promise<ScoringStatsResponse> {
  const response = await apiClient.get<ScoringStatsResponse>('/jobs/score-policies/stats');
  return response.data;
}

interface SyncJobStatus {
  syncId: string;
  status: 'running' | 'completed' | 'failed';
  startedAt: string;
  error?: string;
}

async function triggerSync(forceFullSync = false): Promise<SyncResult> {
  const params = forceFullSync ? '?forceFullSync=true' : '';
  const startResponse = await apiClient.post<{ syncId: string; status: string }>(
    `/jobs/sync-policies${params}`
  );
  const { syncId } = startResponse.data;

  // Poll until completion
  const pollInterval = 5000;
  const maxAttempts = 360; // ~30 minutes
  for (let i = 0; i < maxAttempts; i++) {
    await new Promise((r) => setTimeout(r, pollInterval));
    const pollResponse = await apiClient.get<SyncJobStatus & Partial<SyncResult>>(
      `/jobs/sync-policies/jobs/${syncId}`
    );
    if (pollResponse.data.status === 'completed') {
      return pollResponse.data as unknown as SyncResult;
    }
    if (pollResponse.data.status === 'failed') {
      throw new Error(pollResponse.data.error || 'Sync failed');
    }
  }
  throw new Error('Sync timed out after 30 minutes');
}

async function fetchIngestionStatus(): Promise<IngestionStatusResponse> {
  const response = await apiClient.get<IngestionStatusResponse>('/jobs/ingest-rates/status');
  return response.data;
}

interface IngestionJobStatus {
  logId: string;
  status: 'running' | 'completed' | 'failed';
  startedAt: string;
  error?: string;
}

async function triggerIngestion(types?: string[]): Promise<IngestionResult> {
  const startResponse = await apiClient.post<{ logId: string; status: string }>(
    '/jobs/ingest-rates',
    { types: types ?? ['FIA', 'MYGA'] }
  );
  const { logId } = startResponse.data;

  // Poll until completion
  const pollInterval = 5000;
  const maxAttempts = 360; // ~30 minutes
  for (let i = 0; i < maxAttempts; i++) {
    await new Promise((r) => setTimeout(r, pollInterval));
    const pollResponse = await apiClient.get<IngestionJobStatus & Partial<IngestionResult>>(
      `/jobs/ingest-rates/jobs/${logId}`
    );
    if (pollResponse.data.status === 'completed') {
      return pollResponse.data as unknown as IngestionResult;
    }
    if (pollResponse.data.status === 'failed') {
      throw new Error(pollResponse.data.error || 'Ingestion failed');
    }
  }
  throw new Error('Ingestion timed out after 30 minutes');
}

async function fetchDbStatus(): Promise<DbStatusResponse> {
  const response = await apiClient.get<DbStatusResponse>('/jobs/debug/db-status');
  return response.data;
}

async function fetchHealthConsole(): Promise<HealthConsoleResponse> {
  const response = await apiClient.get<HealthConsoleResponse>('/admin/health');
  return response.data;
}

interface ScoringJobStatus {
  jobId: string;
  status: 'running' | 'completed' | 'failed';
  startedAt: string;
  error?: string;
}

async function triggerScoring(): Promise<ScoringResult> {
  const startResponse = await apiClient.post<{ jobId: string; status: string }>(
    '/jobs/score-policies'
  );
  const { jobId } = startResponse.data;

  // Poll until completion
  const pollInterval = 5000;
  const maxAttempts = 360; // ~30 minutes
  for (let i = 0; i < maxAttempts; i++) {
    await new Promise((r) => setTimeout(r, pollInterval));
    const pollResponse = await apiClient.get<ScoringJobStatus & Partial<ScoringResult>>(
      `/jobs/score-policies/jobs/${jobId}`
    );
    if (pollResponse.data.status === 'completed') {
      return pollResponse.data as unknown as ScoringResult;
    }
    if (pollResponse.data.status === 'failed') {
      throw new Error(pollResponse.data.error || 'Scoring failed');
    }
  }
  throw new Error('Scoring timed out after 30 minutes');
}

// Hooks
export function useSyncStatus(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: pipelineKeys.syncStatus(),
    queryFn: fetchSyncStatus,
    staleTime: 30_000,
    refetchInterval: options?.refetchInterval,
  });
}

export function useScoringStats(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: pipelineKeys.scoringStats(),
    queryFn: fetchScoringStats,
    staleTime: 30_000,
    refetchInterval: options?.refetchInterval,
  });
}

export function useTriggerSync() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: triggerSync,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.syncStatus() });
    },
  });
}

export function useDbStatus() {
  return useQuery({
    queryKey: pipelineKeys.dbStatus(),
    queryFn: fetchDbStatus,
    staleTime: 10_000,
  });
}

export function useTriggerScoring() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: triggerScoring,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.scoringStats() });
    },
  });
}

export function useIngestionStatus(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: pipelineKeys.ingestionStatus(),
    queryFn: fetchIngestionStatus,
    staleTime: 30_000,
    refetchInterval: options?.refetchInterval,
  });
}

export function useHealthConsole(options?: {
  refetchInterval?: number | false;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: pipelineKeys.healthConsole(),
    queryFn: fetchHealthConsole,
    staleTime: 10_000,
    refetchInterval: options?.refetchInterval,
    enabled: options?.enabled,
  });
}

export function useTriggerIngestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: triggerIngestion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.ingestionStatus() });
    },
  });
}
